import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import apiFetch from 'src/Common/api/auth/Auth';
import ApiBaseUrl from 'src/Common/ApiBaseURL';

// Structured in-app notification (matches the server NotificationService payload + feed rows).
export interface AppNotification {
  id: number;
  title: string;
  message: string;
  type: number;        // NotificationCategory (0 General,1 Security,2 Billing,3 AppUpdate,...)
  severity?: string;   // info | success | warning | error
  icon?: string;       // icon key the bell maps to an MUI icon
  link?: string;       // route to navigate to on click
  createdDate: string;
  isRead: boolean;
  scope?: string;      // All | Country | Company | Store | User
}

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  markAsRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  clearAll: () => Promise<void>;
  refresh: () => Promise<void>;
  connectionStatus: 'Connected' | 'Connecting' | 'Disconnected';
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  markAsRead: async () => {},
  markAllRead: async () => {},
  clearAll: async () => {},
  refresh: async () => {},
  connectionStatus: 'Disconnected'
});

// Resolve the active token the same way apiFetch does (honours developer impersonation).
const getToken = () =>
  sessionStorage.getItem('impersonateToken') || localStorage.getItem('authToken');

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionStatus, setConnectionStatus] =
    useState<'Connected' | 'Connecting' | 'Disconnected'>('Disconnected');
  const [token, setToken] = useState<string | null>(getToken());
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ── Load the per-user feed (bell shows the most recent 20) ──
  const refresh = useCallback(async () => {
    if (!getToken()) return;
    try {
      const data: any = await apiFetch('Notification/feed?page=1&pageSize=20');
      if (data && Array.isArray(data.data)) {
        setNotifications(data.data as AppNotification[]);
        setUnreadCount(typeof data.unreadCount === 'number' ? data.unreadCount : 0);
      }
    } catch {
      /* silent — feed endpoint may be unavailable (e.g. pre-migration) */
    }
  }, []);

  // ── Initial load ──
  useEffect(() => {
    refresh();
  }, [refresh]);

  // ── Connection factory ──
  const createConnection = useCallback(() => {
    const t = getToken();
    if (!t) return null;
    return new HubConnectionBuilder()
      .withUrl(`${ApiBaseUrl.signalRBaseUrl}`, {
        accessTokenFactory: () => getToken() || '',
        logger: LogLevel.Warning
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) =>
          retryContext.previousRetryCount === 0 ? 2000 : 5000
      })
      .build();
  }, []);

  // ── Connection lifecycle ──
  useEffect(() => {
    if (!token) return;

    const connection = createConnection();
    if (!connection) return;

    let isMounted = true;

    connection.onreconnecting(() => setConnectionStatus('Connecting'));
    connection.onreconnected(() => {
      setConnectionStatus('Connected');
      refresh(); // re-sync any notifications missed while disconnected
    });
    connection.onclose(() => {
      setConnectionStatus('Disconnected');
      if (isMounted) setTimeout(() => startConnection(connection), 5000);
    });

    // NEW structured channel — the real bell feed.
    connection.on('ReceiveAppNotification', (payloadJson: string) => {
      try {
        const n = JSON.parse(payloadJson) as AppNotification;
        if (!n || typeof n.id === 'undefined') return;
        setNotifications((prev) => {
          if (prev.some((x) => x.id === n.id)) return prev; // de-dupe
          return [{ ...n, isRead: false }, ...prev].slice(0, 50);
        });
        setUnreadCount((c) => c + 1);
      } catch {
        /* ignore malformed payloads */
      }
    });

    // LEGACY channel (title,message) — kept for backward compatibility. Surface as a generic toast.
    connection.on('ReceiveNotification', (title: string, message: string) => {
      // Ignore machine-to-machine sync messages used by KDS/dining clients.
      const sync = ['kitchen', 'dining_order', 'ticket', 'ticketItems', 'diningOrderCancelled'];
      if (typeof title === 'string' && sync.includes(title)) return;
      setNotifications((prev) => [
        {
          id: Date.now(),
          title: title || 'Notification',
          message: message || '',
          type: 0,
          severity: 'info',
          createdDate: new Date().toISOString(),
          isRead: false
        },
        ...prev
      ].slice(0, 50));
      setUnreadCount((c) => c + 1);
    });

    // Forced logout pushed by the developer Session Monitor — sign out immediately.
    connection.on('ForceLogout', (reason: string) => {
      try { connection.stop(); } catch { /* ignore */ }
      sessionStorage.removeItem('profileData');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRoles');
      localStorage.removeItem('employeeProfile');
      localStorage.removeItem('permissionGroups');
      try {
        sessionStorage.setItem('forceLogoutReason', reason || 'Your session was ended by an administrator.');
      } catch { /* ignore */ }
      window.location.replace('/login');
    });

    const startConnection = async (conn: HubConnection) => {
      try {
        setConnectionStatus('Connecting');
        await conn.start();
        if (isMounted) setConnectionStatus('Connected');
      } catch {
        if (isMounted) {
          setConnectionStatus('Disconnected');
          setTimeout(() => startConnection(conn), 5000);
        }
      }
    };

    startConnection(connection);

    return () => {
      isMounted = false;
      connection.stop();
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [token, createConnection, refresh]);

  // ── React to auth changes (login/logout in this or another tab) ──
  useEffect(() => {
    const sync = () => {
      const t = getToken();
      setToken(t);
      if (!t) {
        setNotifications([]);
        setUnreadCount(0);
        setConnectionStatus('Disconnected');
      }
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  // ── Mark a single notification read ──
  const markAsRead = useCallback(async (id: number) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await apiFetch(`Notification/read/${id}`, { method: 'POST' });
    } catch {
      /* local state already updated; ignore (e.g. synthetic legacy id) */
    }
  }, []);

  // ── Mark everything read ──
  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try {
      await apiFetch('Notification/read-all', { method: 'POST' });
    } catch {
      /* ignore */
    }
  }, []);

  // ── Clear / delete everything ──
  const clearAll = useCallback(async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await apiFetch('Notification/clear-all', { method: 'POST' });
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, markAsRead, markAllRead, clearAll, refresh, connectionStatus }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
